import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { LoginInput, RegisterInput } from '@en3/shared';
import { authApi } from '@/api/auth.api';
import { queryKeys } from '@/api/client';
import { useAuthStore } from '@/stores/auth.store';

/**
 * 认证相关的 hooks。
 *
 * 约定：**所有会改变登录态的操作都走 store action**（store 是唯一真源），
 * 这里只负责把它们包成 TanStack Query 的 mutation，让 UI 拿到 isPending / error 等状态。
 */

/** 应用启动时恢复登录态（在 Providers 里调用一次）。 */
export function useBootstrapAuth(): void {
  const status = useAuthStore((s) => s.status);
  const bootstrap = useAuthStore((s) => s.bootstrap);

  useEffect(() => {
    if (status === 'idle') void bootstrap();
  }, [status, bootstrap]);
}

export function useAuthUser() {
  return useAuthStore((s) => s.user);
}

export function useAuthStatus() {
  return useAuthStore((s) => s.status);
}

export function useIsAuthenticated() {
  return useAuthStore((s) => s.status === 'authenticated');
}

/** 需要登录的页面用它取「当前用户 + 统计」，未登录时不发请求。 */
export function useMe() {
  const status = useAuthStore((s) => s.status);
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => authApi.me(),
    enabled: status === 'authenticated',
  });
}

export function useLogin() {
  const login = useAuthStore((s) => s.login);
  return useMutation({ mutationFn: (input: LoginInput) => login(input) });
}

export function useRegister() {
  const register = useAuthStore((s) => s.register);
  return useMutation({ mutationFn: (input: RegisterInput) => register(input) });
}

export function useLogout() {
  const logout = useAuthStore((s) => s.logout);
  return useMutation({ mutationFn: (allDevices?: boolean) => logout(allDevices ?? false) });
}
